import { DatePipe, NgIf } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChartComponent } from '@next/core-lib/chart';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { DataSerie, DataSeries } from '@next/core-lib/models';
import * as Highcharts from 'highcharts';
import { Cycle, Section } from 'src/app/cycle/models/cycle.model';
import { ReferenceValueSheetDataSeries } from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import Utils from 'src/app/utils';

import { Parameter } from '../../models/performance.model';

interface ChartPointProperties {
  date: number;
  dayNr: number;
  weekNr: string | number;
  sectionName: string;
  unit?: string;
}

export interface PerformanceKpiChartDialogComponentInputData extends AbstractDialogData {
  kpi: Parameter;
  cycle: Cycle;
  sections: Section[];
  sectionsDataSeries: DataSeries<number, number>;
  referenceValueSheetDataSeries: ReferenceValueSheetDataSeries[];
}

@Component({
  selector: 'app-performance-kpi-chart-dialog',
  templateUrl: './performance-kpi-chart-dialog.component.html',
  imports: [DialogComponent, NgIf, ChartComponent, TranslatePipe],
  providers: [DatePipe],
})
export class PerformanceKpiChartDialogComponent extends AbstractDialog<void> implements OnInit {
  chartOptions: Highcharts.Options;
  chartSeries: Highcharts.SeriesOptionsType[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PerformanceKpiChartDialogComponentInputData,
    protected mdDialogRef: MatDialogRef<
      PerformanceKpiChartDialogComponent,
      AbstractDialogResultData<void>
    >,
    private readonly localeService: LocaleService,
    private readonly datePipe: DatePipe,
  ) {
    super();
    this.data.title = `${this.localeService.translate(
      'performance.kpi-chart-title',
    )} ${this.localeService.translate(`performance.kpi.${data.kpi}`)}`;
  }

  ngOnInit(): void {
    if (this.data.kpi && this.data.sections?.length > 0 && this.data.sectionsDataSeries) {
      this.createChart();
    }
  }

  private getInitialChartOptions(): Highcharts.Options {
    const getYaxisOptions = () => {
      switch (this.data.kpi) {
        // Percentages have a max of 100%
        case Parameter.MortalityPercentageCripple:
        case Parameter.MortalityPercentageDeath:
        case Parameter.MortalityPercentageOther:
        case Parameter.MortalityPercentageSelection:
        case Parameter.MortalityPercentageSexError:
        case Parameter.MortalityPercentageDamages:
        case Parameter.CumulativeMortalityPercentage:
        case Parameter.MortalityPercentageAll:
          return {
            name: '',
          };
      }
    };
    const me = this;

    return {
      chart: {
        height: '450px',
        zoomType: 'xy',
        panning: {
          enabled: true,
          type: 'xy',
        },
        panKey: 'shift',
      },
      xAxis: {
        title: {
          enabled: true,
          text: this.localeService.translate('performance.week-age'),
        },
        tickInterval: 7,
        labels: {
          formatter: (s) => `${Math.floor(<number>s.value / 7)}`,
        },
      },
      yAxis: {
        ...getYaxisOptions(),
        plotLines: this.getPlotLines(),
        title: {
          enabled: false,
        },
      },
      tooltip: {
        shared: true,
        headerFormat: '',
        formatter(this: Highcharts.Point) {
          const points = this.points ?? [this];
          const highChartsPoint = points[0] as Highcharts.Point & {
            properties?: ChartPointProperties;
          };
          let html = ` ${me.localeService.translate(
            'performance.week',
          )}: ${highChartsPoint?.properties?.weekNr}</br>${me.localeService.translate(
            'performance.day',
          )}: ${this.x}</br>${me.localeService.translate(
            'performance.date',
          )}: ${me.datePipe.transform(highChartsPoint?.properties?.date)}</br>`;

          points.forEach((p) => {
            const point = p as Highcharts.Point & {
              properties?: ChartPointProperties;
            };
            html += ` <span style="color: ${p.color}">\u25CF</span> ${
              point.properties?.sectionName
            }</br>${me.localeService.translate(
              `performance.kpi.${me.data.kpi.toString()}`,
            )}: <b>${p.y} ${point.properties?.unit ?? ''}</b></br>`;
          });
          return html;
        },
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: true,
          },
        },
      },
      credits: {
        enabled: false,
      },
    } as Highcharts.Options;
  }

  private getPlotLines() {
    switch (this.data.kpi) {
      case Parameter.GrowthComparedToReference:
        return [
          {
            color: '#000000',
            width: 1,
            value: 0,
            zIndex: 2,
          },
        ];
      default:
        return [];
    }
  }

  getEarliestBirthDate(cycle: Cycle): Date {
    let earliestBirthDate: Date;
    cycle.arrivals.forEach((arrival) => {
      const chickBirthDate = Utils.toUtcDate(new Date(arrival.chickBirthDate));
      if (earliestBirthDate === undefined || chickBirthDate < earliestBirthDate) {
        earliestBirthDate = chickBirthDate;
      }
    });
    return earliestBirthDate;
  }

  getBirthDateForSection(cycle: Cycle, sectionId: number): Date {
    let earliestSectionBirthDate: Date;
    cycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (allocation.section.id === sectionId) {
          const chickBirthDate = Utils.toUtcDate(new Date(arrival.chickBirthDate));
          if (earliestSectionBirthDate === undefined || chickBirthDate < earliestSectionBirthDate) {
            earliestSectionBirthDate = chickBirthDate;
          }
        }
      });
    });
    return earliestSectionBirthDate;
  }

  private getDifferenceBirthDateArrivalDateForSection(cycle: Cycle, sectionId: number): number {
    let earliestSectionBirthDate: Date;
    let earliestSectionArrivalDate: Date;
    let difference = 0;
    cycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (allocation.section.id === sectionId) {
          const chickBirthDate = Utils.toUtcDate(new Date(arrival.chickBirthDate));
          const chickArrivalDate = Utils.toUtcDate(new Date(arrival.arrivalDate));
          if (earliestSectionBirthDate === undefined || chickBirthDate < earliestSectionBirthDate) {
            earliestSectionBirthDate = chickBirthDate;
            earliestSectionArrivalDate = chickArrivalDate;
          }
        }
      });
    });

    if (earliestSectionBirthDate && earliestSectionBirthDate) {
      difference =
        (earliestSectionArrivalDate.getTime() - earliestSectionBirthDate.getTime()) /
        (1000 * 3600 * 24);
    }

    return difference;
  }

  private createChart(): void {
    const dataSeriesParam = this.data.sectionsDataSeries.metaData.parameters.find(
      (p) => p.code === this.data.kpi.toString(),
    );
    this.chartSeries = [];
    this.chartOptions = this.getInitialChartOptions();

    this.data.sections.forEach((section) => {
      let sectionDateOffset = 0;

      const earliestBirthDateForSection = this.getBirthDateForSection(this.data.cycle, section.id);
      const earliestBirthDate = this.getEarliestBirthDate(this.data.cycle);

      if (earliestBirthDateForSection && earliestBirthDate) {
        sectionDateOffset =
          (earliestBirthDateForSection.getTime() - earliestBirthDate.getTime()) /
          (1000 * 3600 * 24);
      }

      const differenceBirthDateArrivalDate = this.getDifferenceBirthDateArrivalDateForSection(
        this.data.cycle,
        section.id,
      );

      const sectionSeries = this.data.sectionsDataSeries.series[section.id][0];
      const values = sectionSeries.values
        .filter((c) => !this.isNaN(c.value))
        .map((s) => ({
          x: Number(s.dimensionValue) + differenceBirthDateArrivalDate + 1,
          y: dataSeriesParam?.numberOfDecimals
            ? this.round(s.value, dataSeriesParam?.numberOfDecimals)
            : s.value,
          properties: {
            date: new Date(s.properties['Date'].toString()).setDate(
              Utils.toUtcDate(new Date(s.properties['Date'].toString())).getDate() +
                sectionDateOffset,
            ),
            dayNr: Number(s.properties['DayNr']) + differenceBirthDateArrivalDate + 1,
            weekNr: s.properties['WeekNr'],
            sectionName: `${section.name} (${section.barn.name})`,
            unit: dataSeriesParam?.unit,
          },
        }));
      this.chartSeries.push({
        type: 'line',
        name: `${section.name} (${section.barn.name})`,
        data: values,
      });

      const dataSerie = this.data.referenceValueSheetDataSeries?.find(
        (x) =>
          parseInt(x?.series.name.split('ReferenceValueSheet_', 2)[1], 10) ===
          section.referenceValueSheetId,
      );

      let series: DataSerie<number, number>;
      switch (this.data.kpi.toString()) {
        case 'AnimalWeightInGrams':
          series = dataSerie?.series?.series['ChickWeightInGrams'][0];
          break;
        case 'FeedIntakeInGramsPerAnimalPerDay':
          series = dataSerie?.series?.series['FeedIntakePerChick'][0];
          break;
        case 'WaterIntakeInMililitersPerAnimalPerDay':
          series = dataSerie?.series?.series['WaterIntakePerChick'][0];
          break;
        case 'WaterFeedRatio':
          series = dataSerie?.series?.series['WaterToFeedRatio'][0];
          break;
        case 'NumberOfHoursLight':
          series = dataSerie?.series?.series['NumberOfHoursLight'][0];
          break;
        case 'LightIntensityInLux':
          series = dataSerie?.series?.series['LightIntensityInLux'][0];
          break;
        default:
          break;
      }

      if (series !== undefined) {
        const values = series.values
          .filter((c) => !this.isNaN(c.value))
          .map((s) => ({
            x: s.dimensionValue,
            y: dataSeriesParam?.numberOfDecimals
              ? this.round(s.value, dataSeriesParam?.numberOfDecimals)
              : s.value,
            properties: {
              sectionName: series.name,
              unit: dataSeriesParam?.unit,
            },
          }));
        this.chartSeries.push({
          type: 'line',
          name: `${dataSerie.name}`,
          data: values,
        });
      }
    });
  }

  round(decimal: number, decimalPoints: number): number {
    return Math.round(decimal * Math.pow(10, decimalPoints)) / Math.pow(10, decimalPoints);
  }

  isNaN(value: number): boolean {
    return Number.isNaN(value) || !Number.isFinite(value);
  }
}
